import { Link } from 'react-router';
import type { HistoryYear } from '#shared/types';
import { Badge } from '#client/components/ui/Badge';
import { Card } from '#client/components/ui/Card';
import type { Column } from '#client/components/ui/DataTable';
import { DataTable } from '#client/components/ui/DataTable';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Page } from '#client/components/ui/Page';
import { PageLoading, PageMessage } from '#client/components/ui/PageStatus';
import { useCan } from '#client/hooks/useCan';
import { useApiResource } from '#client/hooks/useResourceItem';
import { formatCount, formatMoney, countOf } from '#client/lib/format';
import { ENDPOINT } from '#client/lib/endpoints';
import { RESOURCE } from '#client/lib/routes';
import { sumHistoryYears } from './reports.logic';

/** The patient list, narrowed to one calendar year. */
function patientsInYear(year: number): string {
  return `${RESOURCE.patients}?from=${year}-01-01&to=${year}-12-31`;
}

/**
 * Every year the lab has data for.
 *
 * The screen the Excel "Patient History" sheet becomes once its rows are
 * imported. Two things it does that the sheet could not:
 *
 *   * **Every row drills through** to the patients registered in that year. A
 *     count nobody can open is a dead end, and the first question after "we saw
 *     4,000 patients in 2019" is always "which ones".
 *   * **Approximate dates are labelled.** An imported row that carried only a
 *     year is filed at 1 July with `date_is_approximate` set, and saying so is
 *     the difference between a reconstructed figure and a fabricated one.
 */
export function YearHistoryPage() {
  const can = useCan();
  const seesMoney = can('billing.view');
  const history = useApiResource<HistoryYear[]>(ENDPOINT.yearHistory);

  if (history.loading) return <PageLoading title="Year-wise history" fullWidth />;
  if (history.error || !history.data) {
    return (
      <PageMessage title="Year-wise history" fullWidth>
        {history.error ?? 'Could not load the history.'}
      </PageMessage>
    );
  }

  const years = history.data;
  const totals = sumHistoryYears(years);

  return (
    <Page
      title="Year-wise history"
      subtitle={`${formatCount(totals.patients)} visits and ${formatCount(totals.reports)} tests across ${countOf(years.length, 'year')}`}
      fullWidth
    >
      {years.length === 0 ? (
        <EmptyState
          title="No visits recorded yet"
          body="Register a patient, or import the lab's historical sheets, and the years will appear here."
        />
      ) : (
        <Card flush>
          <DataTable
            columns={columns(seesMoney)}
            rows={years}
            rowKey={(row) => row.year}
            caption="Visits, tests and revenue by year"
          />
        </Card>
      )}
    </Page>
  );
}

function columns(seesMoney: boolean): Column<HistoryYear>[] {
  return [
    {
      key: 'year',
      header: 'Year',
      width: '110px',
      frozen: true,
      // The whole calendar year, which is what the row counted — the server
      // buckets in the lab's timezone, so these bounds match its grouping.
      render: (row) => (
        <Link to={patientsInYear(row.year)}>
          <strong>{row.year}</strong>
        </Link>
      ),
    },
    {
      key: 'patients',
      header: 'Visits',
      width: '130px',
      align: 'end',
      render: (row) => <span className="tnum">{formatCount(row.patients)}</span>,
    },
    {
      key: 'reports',
      header: 'Tests',
      width: '130px',
      align: 'end',
      render: (row) => <span className="tnum">{formatCount(row.reports)}</span>,
    },
    ...(seesMoney
      ? [
          {
            key: 'billed',
            header: 'Billed',
            width: '160px',
            align: 'end' as const,
            render: (row: HistoryYear) => <span className="tnum">{formatMoney(row.billed)}</span>,
          },
          {
            key: 'collected',
            header: 'Collected',
            width: '160px',
            align: 'end' as const,
            render: (row: HistoryYear) => (
              <span className="tnum">{formatMoney(row.collected)}</span>
            ),
          },
        ]
      : []),
    {
      key: 'approximate',
      header: 'Dates',
      width: '190px',
      render: (row) =>
        row.approximate === 0 ? (
          <span className="text-fg-muted">Exact</span>
        ) : (
          <Badge tone="warning">{formatCount(row.approximate)} approximate</Badge>
        ),
    },
  ];
}
